import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, Home, LayoutGrid, Settings2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/utils/cn";
import type { ComercialUnitRow } from "@/services/comercial";
import type { ChaoMapaCell, ChaoMapaPayload, ChaoStatusCelula } from "@/services/chaoDeObra";
import {
  fetchChaoMapa,
  saveChaoMapaProgressBatch,
  saveChaoPrevisoesBatch,
} from "@/services/chaoDeObra";
import { listUnitBadge } from "./chaoUnitCardUtils";
import { ChaoAvancoMapaDrawer } from "./ChaoAvancoMapaDrawer";
import { ChaoAvancoMapaMedicoesModal } from "./ChaoAvancoMapaMedicoesModal";
import {
  formatMedicaoRange,
  getStatusCelula,
  shortEapCode,
  shortServiceName,
  STATUS_CELULA_LABELS,
} from "./chaoAvancoMapaStatus";

interface HouseCol {
  unit: ComercialUnitRow;
  number: string;
  sort: number;
}

interface Props {
  costCenterId: string;
  units: ComercialUnitRow[];
  canEditPlanejamento: boolean;
  canAtestar: boolean;
  onProgressChanged?: () => void;
}

const LEGENDA: { status: ChaoStatusCelula; label: string }[] = [
  { status: "nao_iniciado", label: "Não iniciado" },
  { status: "previsto", label: "Previsto" },
  { status: "execucao", label: "Em execução" },
  { status: "concluido", label: "Concluído" },
  { status: "atestado", label: "Atestado" },
  { status: "atrasado", label: "Atrasado" },
  { status: "pendencia", label: "Pendência" },
];

function cellKey(serviceId: string, unitId: string): string {
  return `${serviceId}::${unitId}`;
}

function parseKey(k: string): { serviceId: string; unitId: string } {
  const [serviceId, unitId] = k.split("::");
  return { serviceId: serviceId ?? "", unitId: unitId ?? "" };
}

export function ChaoAvancoMapaPanel({
  costCenterId,
  units,
  canEditPlanejamento,
  canAtestar,
  onProgressChanged,
}: Props) {
  const [medicaoId, setMedicaoId] = useState<string | null>(null);
  const [payload, setPayload] = useState<ChaoMapaPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterAtrasados, setFilterAtrasados] = useState(false);
  const [filterPendencias, setFilterPendencias] = useState(false);
  const [statusFilter, setStatusFilter] = useState<ChaoStatusCelula | "">("");
  const [serviceQuery, setServiceQuery] = useState("");
  const [pctMin, setPctMin] = useState("");
  const [pctMax, setPctMax] = useState("");
  const [showPct, setShowPct] = useState(false);
  const [highlightRow, setHighlightRow] = useState<string | null>(null);
  const [highlightCol, setHighlightCol] = useState<string | null>(null);
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const dragRef = useRef(false);
  const [drawer, setDrawer] = useState<{ unitId: string; serviceId: string } | null>(null);
  const [medicoesOpen, setMedicoesOpen] = useState(false);
  const [batchBusy, setBatchBusy] = useState(false);

  const houses: HouseCol[] = useMemo(() => {
    return units
      .map((unit) => {
        const number = listUnitBadge(unit.name, unit.id);
        const sort = parseInt(number, 10);
        return { unit, number, sort: Number.isFinite(sort) ? sort : Number.MAX_SAFE_INTEGER };
      })
      .sort((a, b) => a.sort - b.sort || a.number.localeCompare(b.number, "pt-BR"));
  }, [units]);

  const unitIds = useMemo(() => houses.map((h) => h.unit.id), [houses]);

  const load = useCallback(async () => {
    if (!costCenterId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchChaoMapa({ costCenterId, unitIds, medicaoId });
      if (!data.success) {
        setError(data.error ?? "Não foi possível carregar o mapa.");
        return;
      }
      setPayload(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível carregar o mapa.");
    } finally {
      setLoading(false);
    }
  }, [costCenterId, unitIds, medicaoId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = payload?.rows ?? [];
  const cells = payload?.cells ?? {};
  const columns = payload?.columns ?? [];
  const colPct = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of columns) m.set(c.unitId, c.generalPct);
    return m;
  }, [columns]);

  const q = serviceQuery.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    return rows.filter((row) => {
      if (q) {
        const name = `${row.code} ${shortServiceName(row.name)}`.toLowerCase();
        if (!name.includes(q)) return false;
      }
      const rowCells = houses.map((h) => cells[row.serviceId]?.[h.unit.id]);
      if (filterAtrasados && !rowCells.some((c) => c?.status === "atrasado")) return false;
      if (filterPendencias && !rowCells.some((c) => c?.status === "pendencia")) return false;
      if (statusFilter && !rowCells.some((c) => c?.status === statusFilter)) return false;
      const min = pctMin === "" ? null : Number(pctMin);
      const max = pctMax === "" ? null : Number(pctMax);
      if (min != null && Number.isFinite(min) && !rowCells.some((c) => (c?.percentualReal ?? 0) >= min)) {
        return false;
      }
      if (max != null && Number.isFinite(max) && !rowCells.some((c) => (c?.percentualReal ?? 0) <= max)) {
        return false;
      }
      return true;
    });
  }, [rows, q, houses, cells, filterAtrasados, filterPendencias, statusFilter, pctMin, pctMax]);

  const patchCell = (serviceId: string, unitId: string, percentualReal: number) => {
    setPayload((prev) => {
      if (!prev) return prev;
      const prevCell = prev.cells[serviceId]?.[unitId];
      if (!prevCell) return prev;
      const nextCell: ChaoMapaCell = {
        ...prevCell,
        percentualReal,
        status: getStatusCelula({
          percentualReal,
          percentualPrevistoAteMedicaoAtual: prevCell.percentualPrevistoAteMedicaoAtual,
          percentualPrevistoMedicaoSelecionada: prevCell.percentualPrevistoMedicaoSelecionada,
          percentualAtestado: prevCell.percentualAtestado,
          pendencia: !!prevCell.pendencia,
          toleranciaAtrasoPp: prev.atrasoTolerancePp,
        }),
      };
      return {
        ...prev,
        cells: {
          ...prev.cells,
          [serviceId]: { ...prev.cells[serviceId], [unitId]: nextCell },
        },
      };
    });
  };

  const applyBatch = async (pct: number) => {
    if (selection.size === 0 || !payload?.canEditPct) return;
    const items = [...selection].map((k) => {
      const { serviceId, unitId } = parseKey(k);
      return { unitId, serviceId, percentual: pct };
    });
    const snapshot = payload;
    for (const it of items) patchCell(it.serviceId, it.unitId, pct);
    setBatchBusy(true);
    const res = await saveChaoMapaProgressBatch(items, medicaoId ?? undefined);
    setBatchBusy(false);
    if (!res.success) {
      setPayload(snapshot);
      setError(res.error ?? "Falha ao aplicar percentual.");
      return;
    }
    onProgressChanged?.();
    void load();
  };

  const onCellClick = (serviceId: string, unitId: string, shift: boolean) => {
    const k = cellKey(serviceId, unitId);
    if (shift) {
      setSelection((prev) => {
        const next = new Set(prev);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        return next;
      });
      return;
    }
    if (!dragRef.current) {
      setSelection(new Set([k]));
      setDrawer({ unitId, serviceId });
    }
  };

  const drawerCell =
    drawer && payload ? payload.cells[drawer.serviceId]?.[drawer.unitId] : undefined;
  const drawerHouse = drawer ? houses.find((h) => h.unit.id === drawer.unitId) : undefined;
  const drawerRow = drawer ? rows.find((r) => r.serviceId === drawer.serviceId) : undefined;

  const summary = payload?.summary;
  const nCols = houses.length;
  const gridCols = `220px 128px repeat(${Math.max(nCols, 1)}, 28px)`;

  const tooltipFor = (rowName: string, house: HouseCol, cell: ChaoMapaCell | undefined) => {
    const pct = cell?.percentualReal ?? 0;
    const prev = cell?.percentualPrevistoMedicaoSelecionada;
    const when = cell?.lastUpdate
      ? `atualizado em ${new Date(cell.lastUpdate.at).toLocaleDateString("pt-BR")} por ${cell.lastUpdate.usuarioNome}`
      : "sem atualização";
    const prevTxt =
      prev != null && payload?.selectedMedicaoId
        ? `previsto ${prev}% na M${payload.medicoes.find((m) => m.id === payload.selectedMedicaoId)?.numero}`
        : cell?.percentualPrevistoAteMedicaoAtual != null
          ? `previsto ${cell.percentualPrevistoAteMedicaoAtual}%`
          : "sem previsão";
    return `Casa ${house.number} · ${shortServiceName(rowName)} — ${pct}% · ${prevTxt} · ${when}`;
  };

  return (
    <div className="flex flex-col gap-3 min-w-0">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            onClick={() => setMedicaoId(null)}
            className={cn(
              "h-8 px-3 rounded-full text-xs font-semibold border",
              medicaoId == null
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
            )}
          >
            Todos
          </button>
          {(payload?.medicoes ?? []).map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMedicaoId(m.id)}
              className={cn(
                "h-8 px-3 rounded-full text-xs font-semibold border",
                medicaoId === m.id
                  ? "bg-slate-900 text-white border-slate-900"
                  : m.isCurrent
                    ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-300",
              )}
            >
              M{m.numero} · {m.previstosAcumulados}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFilterAtrasados((v) => !v)}
            className={cn(
              "h-8 px-3 rounded-full text-xs font-semibold border",
              filterAtrasados
                ? "bg-red-50 text-red-700 border-red-200"
                : "bg-white text-slate-500 border-slate-200",
            )}
          >
            atrasados
          </button>
          <button
            type="button"
            onClick={() => setFilterPendencias((v) => !v)}
            className={cn(
              "h-8 px-3 rounded-full text-xs font-semibold border",
              filterPendencias
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-white text-slate-500 border-slate-200",
            )}
          >
            pendências
          </button>
          {canEditPlanejamento && (
            <Button
              size="sm"
              variant="ghost"
              leftIcon={<Settings2 className="w-4 h-4" />}
              onClick={() => setMedicoesOpen(true)}
            >
              Medições
            </Button>
          )}
        </div>
      </div>

      {summary && (
        <p className="text-sm text-slate-600">
          {summary.medicaoNumero != null ? (
            <>
              Medição {summary.medicaoNumero} ({formatMedicaoRange(summary.dataInicio, summary.dataFim)})
              {" — "}
            </>
          ) : (
            "Todas as medições — "
          )}
          previstos {summary.previstos} · concluídos {summary.concluidos} · faltam {summary.faltam} ·
          avanço físico {summary.avancoFisico}% (previsto {summary.previstoPct}%)
          {summary.lastAtestadaNumero != null
            ? ` · atestado até a medição ${summary.lastAtestadaNumero}`
            : ""}
        </p>
      )}

      {highlightCol && (
        <p className="text-xs text-slate-500">
          Casa {houses.find((h) => h.unit.id === highlightCol)?.number}: avanço{" "}
          {Math.round(colPct.get(highlightCol) ?? 0)}% · {houses.find((h) => h.unit.id === highlightCol)?.unit.name}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={serviceQuery}
          onChange={(e) => setServiceQuery(e.target.value)}
          placeholder="Filtrar serviço"
          className="h-8 w-48 rounded-lg border border-slate-200 bg-white px-3 text-xs"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as ChaoStatusCelula | "")}
          className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs"
        >
          <option value="">Todos os status</option>
          {LEGENDA.map((l) => (
            <option key={l.status} value={l.status}>
              {l.label}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600">
          <input type="checkbox" checked={showPct} onChange={(e) => setShowPct(e.target.checked)} />
          Mostrar %
        </label>
        <input
          type="number"
          min={0}
          max={100}
          value={pctMin}
          onChange={(e) => setPctMin(e.target.value)}
          placeholder="min %"
          className="h-8 w-16 rounded-lg border border-slate-200 bg-white px-2 text-xs"
        />
        <input
          type="number"
          min={0}
          max={100}
          value={pctMax}
          onChange={(e) => setPctMax(e.target.value)}
          placeholder="máx %"
          className="h-8 w-16 rounded-lg border border-slate-200 bg-white px-2 text-xs"
        />
        {selection.size > 1 && payload?.canEditPct && (
          <div className="flex items-center gap-1 ml-auto">
            <span className="text-xs text-slate-500">{selection.size} células</span>
            {[0, 25, 50, 75, 100].map((v) => (
              <Button key={v} size="sm" variant="secondary" disabled={batchBusy} onClick={() => void applyBatch(v)}>
                {v}
              </Button>
            ))}
          </div>
        )}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
      {loading && !payload && <p className="text-sm text-slate-500">Carregando mapa…</p>}

      {nCols === 0 ? (
        <div className="text-center py-10 rounded-2xl border border-dashed border-slate-200 bg-slate-50">
          <Home className="w-10 h-10 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Nenhuma casa para montar a grade</p>
        </div>
      ) : (
        <div className="relative rounded-xl border border-slate-200 bg-white overflow-auto max-h-[min(70vh,720px)]">
          <div
            className="min-w-max"
            style={{ display: "grid", gridTemplateColumns: gridCols, gap: 4, padding: 8 }}
            onMouseUp={() => {
              dragRef.current = false;
            }}
            onMouseLeave={() => {
              dragRef.current = false;
            }}
          >
            <div className="sticky top-0 left-0 z-30 bg-white" />
            <div className="sticky top-0 z-20 bg-white" style={{ left: 224 }} />
            {houses.map((h) => (
              <button
                key={`n-${h.unit.id}`}
                type="button"
                onClick={() => setHighlightCol((c) => (c === h.unit.id ? null : h.unit.id))}
                className={cn(
                  "sticky top-0 z-10 text-[11px] font-semibold text-slate-400 text-center py-0.5 bg-white",
                  highlightCol === h.unit.id && "text-slate-900",
                )}
              >
                {h.number}
              </button>
            ))}

            <div className="sticky top-6 left-0 z-30 bg-white text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 py-1">
              Serviço
            </div>
            <div
              className="sticky top-6 z-20 bg-white text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1 py-1"
              style={{ left: 224 }}
            >
              Feito
            </div>
            {houses.map((h) => {
              const pct = colPct.get(h.unit.id) ?? 0;
              return (
                <div
                  key={`p-${h.unit.id}`}
                  className={cn(
                    "sticky top-6 z-10 text-[11px] font-semibold text-center py-0.5 bg-white",
                    pct >= 70 ? "text-emerald-600" : "text-slate-500",
                    highlightCol === h.unit.id && "bg-emerald-50",
                  )}
                >
                  {Math.round(pct)}
                </div>
              );
            })}

            {visibleRows.map((row) => {
              const code = shortEapCode(
                { id: row.serviceId, code: row.code, parentId: row.parentId },
                rows.map((r) => ({
                  id: r.serviceId,
                  code: r.code,
                  parentId: r.parentId,
                  isGroup: false,
                })),
              );
              return (
                <Fragment key={row.serviceId}>
                  <button
                    type="button"
                    onClick={() => setHighlightRow((r) => (r === row.serviceId ? null : row.serviceId))}
                    className={cn(
                      "sticky left-0 z-20 flex items-center gap-1.5 bg-white px-1 text-left h-[26px]",
                      highlightRow === row.serviceId && "bg-emerald-50",
                    )}
                    title={shortServiceName(row.name)}
                  >
                    <span className="text-[11px] font-semibold text-slate-500 shrink-0 w-9">{code}</span>
                    <span className="text-[11px] text-slate-700 truncate">{shortServiceName(row.name)}</span>
                  </button>
                  <div
                    className={cn(
                      "sticky z-20 bg-white text-[10px] text-slate-500 px-1 flex items-center h-[26px]",
                      highlightRow === row.serviceId && "bg-emerald-50",
                    )}
                    style={{ left: 224 }}
                  >
                    {row.concludedCount} de {row.total} · {Math.round(row.avgPct)}%
                  </div>
                  {houses.map((h) => {
                    const cell = cells[row.serviceId]?.[h.unit.id];
                    const k = cellKey(row.serviceId, h.unit.id);
                    const selected = selection.has(k);
                    const status = cell?.status ?? "nao_iniciado";
                    const pct = cell?.percentualReal ?? 0;
                    const fillPct =
                      status === "previsto" || status === "pendencia" ? Math.max(pct, 100) : pct;
                    const dim =
                      (filterAtrasados && status !== "atrasado") ||
                      (filterPendencias && status !== "pendencia") ||
                      (statusFilter && status !== statusFilter);
                    return (
                      <button
                        key={k}
                        type="button"
                        data-status={status}
                        className={cn(
                          "chao-mapa-cell",
                          selected && "is-selected",
                          cell?.inconsistent && "is-inconsistent",
                          dim && "opacity-25",
                          highlightRow === row.serviceId || highlightCol === h.unit.id
                            ? "ring-1 ring-slate-300"
                            : "",
                        )}
                        style={{ ["--pct" as string]: `${fillPct}%` }}
                        title={tooltipFor(row.name, h, cell)}
                        aria-label={tooltipFor(row.name, h, cell)}
                        onMouseDown={(e) => {
                          if (e.shiftKey) {
                            dragRef.current = true;
                            setSelection((prev) => new Set(prev).add(k));
                          } else {
                            dragRef.current = false;
                          }
                        }}
                        onMouseEnter={() => {
                          if (dragRef.current) {
                            setSelection((prev) => new Set(prev).add(k));
                          }
                        }}
                        onClick={(e) => onCellClick(row.serviceId, h.unit.id, e.shiftKey)}
                      >
                        <span className="chao-mapa-cell-fill" />
                        {status === "pendencia" && <span className="chao-mapa-cell-hatch" />}
                        {(status === "atestado" || status === "concluido") && (
                          <span className="chao-mapa-cell-icon">
                            <Check className="w-3 h-3" strokeWidth={3} />
                          </span>
                        )}
                        {status === "atrasado" && (
                          <span className="chao-mapa-cell-icon">
                            <AlertTriangle className="w-3 h-3" />
                          </span>
                        )}
                        {showPct && pct > 0 && pct < 100 && (
                          <span className="chao-mapa-cell-icon text-[8px] font-bold text-slate-800">
                            {Math.round(pct)}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </Fragment>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
        {LEGENDA.map((l) => (
          <span key={l.status} className="inline-flex items-center gap-1.5">
            <span data-status={l.status} className="chao-mapa-cell" style={{ ["--pct" as string]: "100%" }}>
              <span className="chao-mapa-cell-fill" />
              {l.status === "pendencia" && <span className="chao-mapa-cell-hatch" />}
              {(l.status === "atestado" || l.status === "concluido") && (
                <span className="chao-mapa-cell-icon">
                  <Check className="w-3 h-3" strokeWidth={3} />
                </span>
              )}
              {l.status === "atrasado" && (
                <span className="chao-mapa-cell-icon">
                  <AlertTriangle className="w-3 h-3" />
                </span>
              )}
            </span>
            {STATUS_CELULA_LABELS[l.status]}
          </span>
        ))}
      </div>

      {canEditPlanejamento && medicaoId && (
        <p className="text-[11px] text-slate-400">
          Dica: no painel da célula, informe o previsto da medição. Para marcar o serviço inteiro como
          100% previsto nesta medição, use o atalho abaixo.
          <Button
            size="sm"
            variant="ghost"
            className="ml-2"
            onClick={() => {
              if (!medicaoId || !highlightRow) return;
              const items = houses.map((h) => ({
                servicoId: highlightRow,
                casaId: h.unit.id,
                medicaoId,
                percentualPrevisto: 100,
              }));
              void saveChaoPrevisoesBatch(items).then(() => void load());
            }}
            disabled={!highlightRow}
          >
            100% previsto na linha destacada
          </Button>
        </p>
      )}

      {drawer && drawerCell && drawerHouse && drawerRow && (
        <ChaoAvancoMapaDrawer
          open
          onClose={() => setDrawer(null)}
          costCenterId={costCenterId}
          unitId={drawer.unitId}
          unitLabel={drawerHouse.number}
          serviceId={drawer.serviceId}
          serviceName={drawerRow.name}
          cell={drawerCell}
          medicoes={payload?.medicoes ?? []}
          selectedMedicaoId={medicaoId}
          canEditPct={payload?.canEditPct ?? false}
          canEditPlanejamento={payload?.canEditPlanejamento ?? canEditPlanejamento}
          onProgressSaved={(pct) => patchCell(drawer.serviceId, drawer.unitId, pct)}
          onChanged={() => {
            onProgressChanged?.();
            void load();
          }}
        />
      )}

      <ChaoAvancoMapaMedicoesModal
        open={medicoesOpen}
        onClose={() => setMedicoesOpen(false)}
        costCenterId={costCenterId}
        unitIds={unitIds}
        medicoes={payload?.medicoes ?? []}
        canAtestar={payload?.canAtestar ?? canAtestar}
        onChanged={() => void load()}
      />
    </div>
  );
}

export type ChaoObraViewMode = "casas" | "mapa" | "produtividade";

export function ChaoAvancoMapaToggle({
  mode,
  onChange,
}: {
  mode: ChaoObraViewMode;
  onChange: (mode: ChaoObraViewMode) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
      <button
        type="button"
        onClick={() => onChange("casas")}
        className={cn(
          "h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5",
          mode === "casas" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500",
        )}
      >
        <Home className="w-3.5 h-3.5" />
        Casas
      </button>
      <button
        type="button"
        onClick={() => onChange("mapa")}
        className={cn(
          "h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5",
          mode === "mapa" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500",
        )}
      >
        <LayoutGrid className="w-3.5 h-3.5" />
        Mapa
      </button>
      <button
        type="button"
        onClick={() => onChange("produtividade")}
        className={cn(
          "h-8 px-3 rounded-md text-xs font-semibold inline-flex items-center gap-1.5",
          mode === "produtividade" ? "bg-white text-slate-800 shadow-sm" : "text-slate-500",
        )}
      >
        <TrendingUp className="w-3.5 h-3.5" />
        Produtividade
      </button>
    </div>
  );
}
